import {
  Grid,
  makeStyles,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import { QRCodeSVG } from 'qrcode.react';
import Button from 'components/Button';
import GenericMaskedInputField from 'components/GenericMaskedInputField';
import { Form, Formik } from 'formik';
import i18n from 'i18n';
import { authenticateMFARequest } from 'modules/authenticate/actions';
import { MFAData } from 'modules/authenticate/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import * as Yup from 'yup';
import palette from 'theme/palette';
import styles from './styles';

const schema = Yup.object().shape({
  code: Yup.string().required(i18n.t('REQUIRED_FIELD_KEY')),
});

interface Props {
  isLoading: boolean;
  mfa?: MFAData;
  language?: string;
  authenticateMFARequest: (props: { code: string }) => void;
}

const FormMFA: React.FC<Props> = ({
  isLoading,
  mfa,
  authenticateMFARequest,
}) => {
  const classes = makeStyles(styles)();

  const copyToClipboard = (secret: string) => {
    navigator.clipboard.writeText(secret);
  };

  const handleSubmit = (values: any) => {
    authenticateMFARequest({ code: values.code.replaceAll(' ', '') });
  };

  return (
    <Grid>
      <Grid item container xs={12} className={classes.spacingContainer}>
        <Grid item xs={12} className={classes.title}>
          <Typography variant="h3" className={classes.text}>
            {i18n.t('MFA')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" className={classes.subtitleLogin}>
            {i18n.t(
              mfa?.configured ? 'INSERT_MFA_CODE_KEY' : 'CONFIGURE_MFA_KEY'
            )}
          </Typography>
        </Grid>
      </Grid>
      <Formik
        initialValues={{ code: '' }}
        enableReinitialize
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, errors, values, submitForm }) => {
          return (
            <React.Fragment>
              {!mfa?.configured && (
                <Grid container justify="center">
                  <figure className={classes.qrCodeWrapper}>
                    <Grid
                      container
                      alignItems="center"
                      justify="center"
                      style={{ height: '100%' }}
                    >
                      {isLoading || !mfa ? (
                        <CircularProgress size={45} />
                      ) : (
                        <>
                          <QRCodeSVG value={mfa.totp_uri} size={148} />
                          <figcaption>
                            {i18n.t('COPY_CLICK_HERE_KEY')}
                          </figcaption>
                        </>
                      )}
                    </Grid>

                    {mfa?.secret && (
                      <button
                        className="qrCodeButton"
                        onClick={() => copyToClipboard(mfa.secret)}
                        data-copy-text={i18n.t('COPY_KEY')}
                        data-copied-text={i18n.t('COPIED_KEY')}
                      />
                    )}
                  </figure>
                </Grid>
              )}
              <Form>
                <Grid container className={classes.mfa}>
                  <Grid item xs={12}>
                    <GenericMaskedInputField
                      fields={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      item={{
                        gridXs: 12,
                        stateKey: 'code',
                        validateOnChange: true,
                        label: i18n.t('AUTHENTICATION_CODE_KEY'),
                        mask: '9 9 9   9 9 9',
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      className={classes.loginButton}
                      onClick={submitForm}
                      isLoading={isLoading}
                      style={{
                        marginTop: 16,
                        opacity: values.code.length < 13 ? 0.5 : 1,
                        color: palette.white,
                      }}
                      disabled={values.code.length < 13}
                    >
                      {i18n.t('ENTER_KEY')}
                    </Button>
                  </Grid>

                  <Grid container justify="space-between" item xs={12}>
                    <Grid item className={classes.linkContainer} xs={6}>
                      <Typography
                        variant="button"
                        component={Link}
                        to="/"
                        className={classes.link}
                      >
                        <ArrowRightAltIcon className={classes.arrowIcon} />
                        {i18n.t('BACK_KEY')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            </React.Fragment>
          );
        }}
      </Formik>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, isLoading },
}: ApplicationState) => ({
  isLoading,
  mfa: data?.mfa,
});

export default connect(mapStateToProps, {
  authenticateMFARequest,
})(FormMFA);
